import type { ComponentChildren } from "preact";
import type { ChatMessage } from "../hooks/useChat.js";
import { ChatHeader } from "./ChatHeader.js";
import { MessageList } from "./MessageList.js";
import { MessageInput } from "./MessageInput.js";

interface ChatWindowProps {
  messages: ChatMessage[];
  streaming: boolean;
  botName: string;
  botAvatar?: string | null;
  language: string;
  disclaimer?: string | null;
  privacyPolicyUrl?: string | null;
  hideBranding?: boolean;
  onSend: (text: string) => void;
  onRetry?: () => void;
  onClose: () => void;
  onNewChat?: () => void;
  whatsappUrl?: string | null;
  startersSlot?: ComponentChildren;
  followUpSlot?: ComponentChildren;
  salesCtaSlot?: ComponentChildren;
  csatSlot?: ComponentChildren;
  children?: ComponentChildren;
}

export function ChatWindow({ messages, streaming, botName, botAvatar, language, disclaimer, privacyPolicyUrl, hideBranding, onSend, onRetry, onClose, onNewChat, whatsappUrl, startersSlot, followUpSlot, salesCtaSlot, csatSlot, children }: ChatWindowProps) {
  return (
    <div class="flex flex-col h-full bg-[var(--background)] rounded-2xl shadow-lg overflow-hidden">
      <ChatHeader botName={botName} botAvatar={botAvatar} onClose={onClose} onNewChat={onNewChat} whatsappUrl={whatsappUrl} />
      <MessageList messages={messages} streaming={streaming} botName={botName} botAvatar={botAvatar} language={language} onRetry={onRetry} startersSlot={startersSlot} csatSlot={csatSlot}>
        {followUpSlot}
        {salesCtaSlot}
        {children}
      </MessageList>
      <MessageInput onSend={onSend} disabled={streaming} language={language} />
      {disclaimer && (
        <p class="px-3 py-1.5 text-[10px] leading-snug text-center text-[var(--muted-foreground)]/70 border-t border-[var(--border)]">
          {disclaimer}
        </p>
      )}
      {(privacyPolicyUrl || !hideBranding) && (
        <div
          class={`flex items-center px-3.5 py-2 border-t border-[var(--border)] text-[10.5px] gap-3 ${
            privacyPolicyUrl && !hideBranding ? "justify-between" : "justify-center"
          }`}
        >
          {privacyPolicyUrl && (
            <a
              href={privacyPolicyUrl}
              target="_blank"
              rel="noopener noreferrer"
              class="text-[var(--muted-foreground)]/70 hover:text-[var(--muted-foreground)] transition-colors tracking-tight"
            >
              Privacy
            </a>
          )}
          {!hideBranding && (
            <a
              href="https://canarychat.app"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center gap-1 text-[var(--muted-foreground)]/60 hover:text-[var(--muted-foreground)] transition-colors tracking-tight"
            >
              <span>Powered by</span>
              <span class="font-semibold" style={{ color: "#d99a00" }}>Canary</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
}
